import { ArrowRight, SlidersHorizontal, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/app-shell";
import { Disclaimer } from "../components/disclaimer";
import { deleteApiProject } from "../lib/api";
import { deleteProject, loadProject, loadVariants } from "../lib/storage";

const formatEuro = (cents: number) =>
  new Intl.NumberFormat("hr-HR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(
    cents / 100,
  );

export function ResultsPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const project = useMemo(() => loadProject(id), [id]);
  const variants = useMemo(() => loadVariants(id), [id]);
  const [style, setStyle] = useState("Svi stilovi");
  const [budgetOnly, setBudgetOnly] = useState(false);

  if (!project || variants.length === 0) {
    return (
      <AppShell eyebrow="Nema rezultata">
        <div className="empty-state">
          <img src="/images/empty-state-480.webp" width="480" height="480" alt="" />
          <h1>Još nema gotovih prijedloga.</h1>
          <p>Pokreni novi projekt ili se vrati na generiranje.</p>
          <Link className="button button--primary" to="/app/new">
            Novi projekt
          </Link>
        </div>
      </AppShell>
    );
  }

  const visible = variants.filter(
    (variant) =>
      (style === "Svi stilovi" || variant.title.toLowerCase().includes(style.toLowerCase())) &&
      (!budgetOnly || variant.estimatedMinCents <= project.preferences.budgetMax * 100),
  );

  return (
    <AppShell
      eyebrow={project.space}
      backTo="/app/new"
      backLabel="Novi projekt"
      actions={
        <button
          className="icon-button"
          type="button"
          aria-label="Obriši projekt"
          onClick={() => {
            if (window.confirm("Obrisati fotografiju, varijante i demo rezultate ovog projekta?")) {
              if (project.backend === "worker") void deleteApiProject(project.id);
              deleteProject();
              void navigate("/app/new", { replace: true });
            }
          }}
        >
          <Trash2 aria-hidden="true" />
        </button>
      }
    >
      <section className="results-heading">
        <div>
          <p className="section-label">Pet gotovih prijedloga</p>
          <h1>Koji smjer vidiš u svom prostoru?</h1>
          <p>Otvori prijedlog za usporedbu s originalom, detektirane elemente i proizvode.</p>
        </div>
        <div className="result-filters" aria-label="Filteri prijedloga">
          <SlidersHorizontal aria-hidden="true" />
          <label>
            <span className="sr-only">Stil</span>
            <select value={style} onChange={(event) => setStyle(event.target.value)}>
              <option>Svi stilovi</option>
              <option>Mediteranska</option>
              <option>Skandinavska</option>
              <option>Moderna</option>
            </select>
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={budgetOnly}
              onChange={(event) => setBudgetOnly(event.target.checked)}
            />
            <span>Unutar budžeta</span>
          </label>
        </div>
      </section>
      <Disclaimer />
      {visible.length ? (
        <div className="variant-grid">
          {visible.map((variant, index) => (
            <article className="variant-card" key={variant.id}>
              <Link to={`/app/projects/${id}/results/${variant.id}`} aria-label={`Otvori ${variant.title}`}>
                <div className="variant-card__image">
                  <img
                    src={variant.imageUrl}
                    alt={`${variant.title}, AI vizualizacija`}
                    width="1024"
                    height="683"
                  />
                  <span>Prijedlog {String(index + 1).padStart(2, "0")}</span>
                </div>
                <div className="variant-card__body">
                  <h2>{variant.title}</h2>
                  <p>{variant.explanation}</p>
                  <dl>
                    <div>
                      <dt>Okvirni trošak</dt>
                      <dd>
                        {formatEuro(variant.estimatedMinCents)}–{formatEuro(variant.estimatedMaxCents)}
                      </dd>
                    </div>
                    <div>
                      <dt>Pronađeno</dt>
                      <dd>{variant.productCount} demo proizvoda</dd>
                    </div>
                  </dl>
                  <span className="variant-card__link">
                    Pregledaj smjer <ArrowRight aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      ) : (
        <div className="no-results">
          <h2>Nijedan prijedlog ne odgovara tim filterima.</h2>
          <p>Isključi “unutar budžeta” ili odaberi sve stilove.</p>
          <button
            className="button button--outline"
            type="button"
            onClick={() => {
              setBudgetOnly(false);
              setStyle("Svi stilovi");
            }}
          >
            Poništi filtere
          </button>
        </div>
      )}
    </AppShell>
  );
}
